<template>
  <div
    class="winding-temperature-chart avoid-break"
    :class="{ 'winding-temperature-chart-alarmed': chartData.alarmed }"
  >
    <div class="text-center">
      <strong>
        {{ $t('engineering.water-on-paper.winding-temperature') }} {{ windingId }}
      </strong>
    </div>
    <div v-if="hasDataForWinding">
      <e-chart
        class="winding-temperature-chart__chart"
        autoresize
        ref="windingTemperatureChart"
        :options="options"
        @rendered="onRendered"
      />
      <img
        class="winding-temperature-chart__image w-100 avoid-break"
        alt="winding-temperature-chart-image"
        :src="chartImg"
      >
      <div class="winding-temperature-chart__last-register">
        <strong>
          {{ $t('engineering.water-on-paper.last-register') }}:
        </strong>
        <span>
          {{ lastRegister }}
        </span>
      </div>
    </div>
    <s-empty-list v-else />
  </div>
</template>

<script>
import themeService from '@/services/themeService';
import engineeringQuickChartsMixin
  from '@/domain/mixins/installation/engineering/engineeringQuickChartsMixin';
import { WindingTemperatureChartVariables } from 'sigma-mappings';

export default {
  name: 'WindingTemperatureChart',
  mixins: [engineeringQuickChartsMixin],
  props: {
    windingId: {
      type: Number,
      required: true,
    },
  },
  computed: {
    options() {
      const series = this.getChartSeries();
      return {
        backgroundColor: this.bgColor,
        tooltip: {
          show: true,
          confine: false,
          trigger: 'item',
          borderColor: themeService.getStyleVar('color-tooltip-border'),
          formatter: this.getTooltipFormatter,
          axisPointer: {
            type: 'cross',
            label: {
              show: false,
            },
            crossStyle: {
              opacity: 0,
            },
          },
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          axisTick: {
            show: false,
          },
          axisLabel: {
            fontSize: this.labelSize,
          },
          axisLine: {
            show: true,
            onZero: true,
            lineStyle: {
              color: this.fontColor,
            },
          },
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: this.fontColor,
            },
          },
          splitNumber: 3,
          axisLabel: {
            show: true,
            fontSize: this.labelSize,
            color: this.fontColor,
            showMinLabel: false,
            formatter: `{value} ${this.chartData.unit}`,
          },
          splitLine: {
            show: false,
          },
        },
        grid: {
          top: 20,
          bottom: 40,
          right: 30,
          left: 50,
        },
        series,
      };
    },
    hasDataForWinding() {
      return this.chartData.windingId;
    },
  },
  methods: {
    getLineColorByName(name) {
      const variables = {
        [WindingTemperatureChartVariables.BUBBLE_FORMATION_TEMPERATURE]: `${themeService.getStyleVar('color-danger')}8c`,
        [WindingTemperatureChartVariables.ALARM_ACTIVATION_LIMIT]: `${themeService.getStyleVar('color-secondary')}8c`,
        [WindingTemperatureChartVariables.WINDING_TEMPERATURE]: themeService.getPrimaryColor(),
      };
      return variables[name] || themeService.getPrimaryColor();
    },
    onRendered() {
      this.$nextTick(() => {
        this.chartImg = this.isCollapsed || !this.$refs.windingTemperatureChart.height
          ? '#'
          : this.$refs.windingTemperatureChart.getDataURL();
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .winding-temperature-chart
    display: block
    border: 1px solid $color-border-gray
    padding: 10px
    border-radius: 3px
    &-alarmed
      border: none
      box-shadow: 0 0 3px var(--color-danger-light),
      \ inset 0 0 3px var(--color-danger-light) !important
    &__last-register
      width: 100%
      text-align: right
      font-size: .8em
    &__chart
      border: 1px solid var(--color-border-dark)
      border-radius: 5px
      min-height: 210px
      height: max-content
      margin: 10px 0
      width: 100%
    &__image
      display: none
    @media print
      .winding-temperature-chart
        text-align: center
        &__chart
          display: none
        &__image
          display: block
</style>
